import React from 'react';
import PropTypes from 'fe-prop-types';
import {
  AppCardWrapper,
  AppCardMain,
  AppCardLogo,
  AppCardFooter,
  AppCardName,
  AppCardCategory,
  AppCardFocused,
  AppCardNameWrapper,
  AppCardFocusedName,
  AppCardFocusedCategory,
  AppCardDescription,
  AppCardPrice,
  AppCardButtonLink,
  AppCardDetailsLink,
  AppCardDetailsLinkAnchor,
} from './styled';
import { logCustomEvent, AppCardTrackIds } from 'fe-appdir-lib-analytics';
import { isExternalInstall, getButtonLabel } from 'fe-appdir-lib-utils';

class AppCard extends React.Component {
  state = {
    isFocused: false,
  };

  static propTypes = {
    app: PropTypes.shape({
      billing: PropTypes.shape({
        billingInterval: PropTypes.string,
        prices: PropTypes.arrayOf(
          PropTypes.shape({
            currency: PropTypes.string,
            price: PropTypes.number,
          }),
        ),
      }),
      shortDescription: PropTypes.string,
      iconUrl: PropTypes.string,
      installUrl: PropTypes.string,
      name: PropTypes.string,
      primaryCategory: PropTypes.shape({
        id: PropTypes.string,
        categoryTypeId: PropTypes.string,
        description: PropTypes.string,
        name: PropTypes.string,
        slug: PropTypes.string,
      }),
      slug: PropTypes.string,
    }).isRequired,
    large: PropTypes.bool,
    center: PropTypes.bool,
  };

  static defaultProps = {
    large: false,
    center: false,
  };

  componentDidUpdate = (prevProps, prevState) =>
    this.state.isFocused && !prevState.isFocused && this.gaFocusedCallback();

  /**
   * Clicking on the card toggles the focus state,
   * unless the target is a `<button>` or `<a>`
   * @param {SyntheticEvent} event
   * @returns {void}
   */
  toggleIsFocused = (event) => {
    const { nodeName } = event.target;

    this.setState((prevState) =>
      nodeName === 'BUTTON' || nodeName === 'A'
        ? prevState
        : {
            isFocused: !prevState.isFocused,
          },
    );
  };

  /**
   * Pushes a custom event to GA on focus of the app card
   * @returns {void}
   */
  gaFocusedCallback = () => {
    logCustomEvent(AppCardTrackIds.FOCUS_APP, this.props.app.slug);
  };

  /**
   * Gets the price of the USD price.
   * @param {Object[]} prices
   * @returns {Number}
   */
  getUSDPrice = (prices) => {
    const usdPrice = prices.filter((price) => price.currency === 'USD');

    return usdPrice.length > 0 ? usdPrice[0].price : null;
  };

  getCategoryText = (app) =>
    Object.prototype.hasOwnProperty.call(app, 'primaryCategory')
      ? app.primaryCategory.name
      : Object.prototype.hasOwnProperty.call(app, 'categories')
        ? app.categories[0].name
        : null;

  shouldRender = (app) =>
    Object.prototype.hasOwnProperty.call(app, 'billing') &&
    Object.prototype.hasOwnProperty.call(app.billing, 'billingInterval') &&
    Object.prototype.hasOwnProperty.call(app.billing, 'prices') &&
    app.billing.prices.length > 0 &&
    Object.prototype.hasOwnProperty.call(app.billing.prices[0], 'currency') &&
    Object.prototype.hasOwnProperty.call(app.billing.prices[0], 'price') &&
    Object.prototype.hasOwnProperty.call(app, 'primaryCategory') &&
    Object.prototype.hasOwnProperty.call(app.primaryCategory, 'name') &&
    Object.prototype.hasOwnProperty.call(app, 'shortDescription') &&
    Object.prototype.hasOwnProperty.call(app, 'iconUrl') &&
    Object.prototype.hasOwnProperty.call(app, 'installUrl') &&
    Object.prototype.hasOwnProperty.call(app, 'name') &&
    Object.prototype.hasOwnProperty.call(app, 'slug');

  render = () => {
    const { app, large, center } = this.props;
    const { billing, shortDescription, installUrl, iconUrl, name, slug } =
      this.props.app;
    const { isFocused } = this.state;

    return this.shouldRender(app) ? (
      <AppCardWrapper
        tabIndex={0}
        isFocused={isFocused}
        onClick={this.toggleIsFocused}
        onKeyPress={this.toggleIsFocused}
      >
        {isFocused ? (
          <AppCardFocused>
            <AppCardLogo small>
              <img src={iconUrl} alt={`${name} logo`} />
            </AppCardLogo>
            <AppCardFocusedName>{name}</AppCardFocusedName>
            <AppCardFocusedCategory>
              {this.getCategoryText(app)}
            </AppCardFocusedCategory>
            <AppCardDescription>{shortDescription}</AppCardDescription>
            <AppCardPrice>
              {isExternalInstall(installUrl)
                ? 'CONTACT VENDOR FOR PRICING'
                : this.getUSDPrice(billing.prices)
                  ? `$${this.getUSDPrice(billing.prices)}`
                  : 'FREE'}
            </AppCardPrice>
            <AppCardButtonLink
              type="standard"
              href={installUrl}
              gaTrackId={AppCardTrackIds.INSTALL_APP}
              gaTrackInfo={slug}
            >
              {getButtonLabel(installUrl)}
            </AppCardButtonLink>
            <AppCardDetailsLink>
              <AppCardDetailsLinkAnchor
                to={`/apps/${slug}`}
                data-ga-track-id={AppCardTrackIds.APP_DETAILS}
                data-ga-track-info={slug}
              >
                See App Details
              </AppCardDetailsLinkAnchor>
            </AppCardDetailsLink>
          </AppCardFocused>
        ) : (
          <React.Fragment>
            <AppCardMain>
              <AppCardLogo>
                <img src={iconUrl} alt={`${name} logo`} />
              </AppCardLogo>
            </AppCardMain>
            <AppCardFooter center={center}>
              <AppCardNameWrapper>
                <AppCardName large={large}>{name}</AppCardName>
                <AppCardCategory large={large}>
                  {this.getCategoryText(app)}
                </AppCardCategory>
              </AppCardNameWrapper>
            </AppCardFooter>
          </React.Fragment>
        )}
      </AppCardWrapper>
    ) : null;
  };
}

export { AppCard };
